import { Injectable, Provider, Type } from '@angular/core';
import { LeadTaskService } from '../lead-task.service';
import { RoomListComponent } from './room-list/room-list.component';
import { RoomExaminationComponent } from './room-examination/room-examination.component';
import { getDefaultTariffs, RoomExaminationResultInfo, } from '../../../examinationResult.model';
import { ErrorListService } from '../../../reusable-components/error-list/error-list.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { CreateRoomComponent } from './create-room/create-room.component';
import { UsageSpaceInfo } from '../../../lead.model';

export function provideRoomSelectionService(): Provider {
  return {
    provide: RoomSelectionService,
    useFactory: (leadTaskService: LeadTaskService) =>
      new RoomSelectionService(leadTaskService),
    deps: [LeadTaskService],
  };
}

export interface RoomListInputNgComponentOutlet {
  component: Type<unknown>;
  content: RoomListInput;
}

export interface RoomListInput {
  errorListService: ErrorListService;
}

export interface RoomExaminationInputNgComponentOutlet {
  component: Type<unknown>;
  content: RoomExaminationInput;
}

export interface RoomExaminationInput {
  errorListService: ErrorListService;
  roomDraftCopy: RoomExaminationResultInfo;
  id: string;
  usageSpaceInfoCopy: UsageSpaceInfo;
}

export interface CreateRoomInputNGComponentOutlet {
  component: Type<unknown>;
  content: CreateRoomInput;
}

export interface CreateRoomInput {
  errorListService: ErrorListService;
}

export type NgComponentOutletComponents =
  | RoomExaminationInputNgComponentOutlet
  | RoomListInputNgComponentOutlet
  | CreateRoomInputNGComponentOutlet;

@Injectable()
export class RoomSelectionService {
  private readonly ngComponentOutletInput: BehaviorSubject<NgComponentOutletComponents>;
  public readonly ngComponentOutletInput$: Observable<NgComponentOutletComponents>;

  private readonly errorListService: ErrorListService;

  constructor(private leadTaskService: LeadTaskService) {
    this.errorListService = this.leadTaskService.errorListServiceRoomSelection;

    const currentRoomExaminationId =
      leadTaskService.getCurrentRoomExaminationId();
    if (currentRoomExaminationId === undefined) {
      this.ngComponentOutletInput =
        new BehaviorSubject<NgComponentOutletComponents>(
          this.getStartRoomListInput(),
        );
    } else {
      this.ngComponentOutletInput =
        new BehaviorSubject<NgComponentOutletComponents>(
          this.getStartRoomExaminationInput(currentRoomExaminationId),
        );
    }
    this.ngComponentOutletInput$ = this.ngComponentOutletInput.asObservable();
  }

  startCreateRoom() {
    this.ngComponentOutletInput.next({
      component: CreateRoomComponent,
      content: {
        errorListService:
          this.errorListService.deleteAllAndCreate('create-room'),
      },
    });
  }

  startRoomList() {
    this.leadTaskService.setCurrentRoomExaminationId(undefined);
    this.ngComponentOutletInput.next(this.getStartRoomListInput());
  }

  private getStartRoomListInput(): RoomListInputNgComponentOutlet {
    return {
      component: RoomListComponent,
      content: {
        errorListService: this.errorListService.deleteAllAndCreate('room-list'),
      },
    };
  }

  getErrorListService() {
    return this.errorListService;
  }

  startRoomExamination(id: string) {
    this.ngComponentOutletInput.next(this.getStartRoomExaminationInput(id));
  }

  getStartRoomExaminationInput(
    id: string,
  ): RoomExaminationInputNgComponentOutlet {
    this.leadTaskService.setCurrentRoomExaminationId(id);
    const usageSpaceInfoCopy = this.leadTaskService.getUsageSpaceCopy(id);
    let roomDraftCopy = this.leadTaskService.getRoomDraftCopy(id);
    if (roomDraftCopy === undefined) {
      roomDraftCopy = {
        tariffs: getDefaultTariffs(),
        images: [],
        audioUrls: [],
        roomIsClosed: false,
      };
    }
    // Populate name from usage space if not yet persisted (e.g. first examination)
    if (!roomDraftCopy.name) {
      roomDraftCopy.name = usageSpaceInfoCopy.name;
    }
    return {
      component: RoomExaminationComponent,
      content: {
        errorListService:
          this.errorListService.deleteAllAndCreate('room-examination'),
        roomDraftCopy: roomDraftCopy,
        id: id,
        usageSpaceInfoCopy: usageSpaceInfoCopy,
      },
    };
  }

  getCreateRoomInput(): CreateRoomInput {
    const currentNgComponentOutletInput =
      this.getCurrentNgComponentOutletInput();
    if (currentNgComponentOutletInput.component !== CreateRoomComponent) {
      throw new Error('try to access createroom input outside of createroom');
    }
    const roomExaminationInput =
      currentNgComponentOutletInput as CreateRoomInputNGComponentOutlet;

    return roomExaminationInput.content;
  }

  getRoomExaminationInput(): RoomExaminationInput {
    const currentNgComponentOutletInput =
      this.getCurrentNgComponentOutletInput();
    if (currentNgComponentOutletInput.component !== RoomExaminationComponent) {
      throw new Error(
        'try to access roomexamination input outside of roomexamination',
      );
    }
    const roomExaminationInput =
      currentNgComponentOutletInput as RoomExaminationInputNgComponentOutlet;

    return roomExaminationInput.content;
  }

  getRoomListInput(): RoomListInput {
    const currentNgComponentOutletInput =
      this.getCurrentNgComponentOutletInput();
    if (currentNgComponentOutletInput.component != RoomListComponent) {
      throw new Error('try to access roomlist input outside of roomlist');
    }
    const roomListInput =
      currentNgComponentOutletInput as RoomListInputNgComponentOutlet;
    return roomListInput.content;
  }

  getCurrentNgComponentOutletInput() {
    return this.ngComponentOutletInput.value;
  }
}
